import { ensureWorkspaceSession, workspaceAccessEnabled } from "../workspaceAccess";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { externalLinks } from "../externalLinks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { LANGUAGE_MESSAGE_TYPE, useI18n } from "../i18n";
import { getDataspaceId } from "../config/runtimeConfig";
import { getApplicationIcon } from "../config/appIcons";
import { buildExternalTargetUrl } from "./externalTargetUrl";
import ContentLoadingState from "./ContentLoadingState";
import "./ExternalHost.css";

export default function ExternalHost({ webId }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const { language, t } = useI18n();
  const activeLink = externalLinks.find((link) => link.slug === slug);
  const [loaded, setLoaded] = useState(false);
  const protectedApp = workspaceAccessEnabled() && ["node-red", "plasma"].includes(slug);
  const [access, setAccess] = useState(null);
  useEffect(() => {
    let active = true;
    setAccess(null);
    if (protectedApp) ensureWorkspaceSession().then(() => { if (active) setAccess({ webId, slug }); })
      .catch(() => { if (active) setAccess({ webId, slug, error: true }); });
    return () => { active = false; };
  }, [protectedApp, webId, slug]);
  const workspaceReady = !protectedApp || (access?.webId === webId && access?.slug === slug && !access?.error);


  const isParentHttps =
    typeof window !== "undefined" && window.location.protocol === "https:";

  const targetIsHttp = useMemo(() => {
    try {
      return new URL(activeLink?.url || "", window.location.origin).protocol === "http:";
    } catch {
      return false;
    }
  }, [activeLink]);

  const targetUrl = useMemo(() => {
    return buildExternalTargetUrl({
      activeLink,
      language,
      origin: window.location.origin,
      dataspaceId: getDataspaceId(),
    });
  }, [activeLink, language]);

  useEffect(() => {
    setLoaded(false);
  }, [targetUrl]);

  useEffect(() => {
    const frameWindow = iframeRef.current?.contentWindow;
    if (!frameWindow) return;
    let targetOrigin = "*";
    try {
      targetOrigin = new URL(targetUrl, window.location.origin).origin;
    } catch {
      targetOrigin = "*";
    }
    frameWindow.postMessage(
      { type: LANGUAGE_MESSAGE_TYPE, language },
      targetOrigin
    );
  }, [language, targetUrl]);

  if (!activeLink) {
    return (
      <div className="external-host-container">
        <div className="toolbar">
          <div className="crumb">
            <span>{t("Not found")}</span>
          </div>
          <div className="primary-actions">
            <button className="pill-btn" onClick={() => navigate(-1)}>
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>{t("Back")}</span>
            </button>
          </div>
        </div>
        <p>{t("Unknown external link.")}</p>
      </div>
    );
  }

  const cannotEmbed = isParentHttps && targetIsHttp;
  const useAppFrame = true;

  return (
    <div className="external-host-container">
      <div className="toolbar toolbar--title external-host-header">
        <div className="crumb">
          <FontAwesomeIcon
            icon={getApplicationIcon(activeLink.slug)}
            className="crumb-icon"
          />
          <span>{activeLink.title}</span>
        </div>

        <div className="primary-actions">
          {activeLink.version ? (
            <span className="integrated-version">{activeLink.version}</span>
          ) : null}
          {!activeLink.hideOpenButton ? (
            <a
              className="pill-btn"
              href={targetUrl}
              target="_blank"
              rel="noreferrer"
            >
              <FontAwesomeIcon icon={faUpRightFromSquare} />
              <span>{t("Open")}</span>
            </a>
          ) : null}
        </div>
      </div>

      <div
        className={`external-host-shell${
          useAppFrame ? " external-host-shell--framed" : ""
        }`}
      >
        {protectedApp && !workspaceReady ? (
          <div role={access?.error ? "alert" : "status"}>
            {t(access?.error ? "Workspace login failed. Please sign in again." : "Loading your personal application workspace …")}
          </div>
        ) : cannotEmbed ? (
          <div className="notice">
            <p>
              This site uses HTTP and cannot be embedded inside an HTTPS app.
              Open it in a new tab, or serve it via HTTPS or an HTTPS reverse proxy.
            </p>
          </div>
        ) : (
          <>
            {!loaded && (
              <ContentLoadingState
                title={activeLink.title}
                message={t("Loading your personal application workspace …")}
                appId={activeLink.slug}
                variant="overlay"
              />
            )}
            <div className="external-host-frame">
              <iframe
                ref={iframeRef}
                src={targetUrl}
                title={activeLink.title}
                onLoad={() => setLoaded(true)}
                style={{ width: "100%", height: "100%", border: 0 }}
                referrerPolicy="no-referrer-when-downgrade"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-downloads allow-top-navigation-by-user-activation allow-popups-to-escape-sandbox"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
